import { useCallback, useEffect, useMemo, useState } from "react";

import { useRouter } from "next/navigation";
import { throttle } from "throttle-debounce";

import { buildCheckoutUrl } from "@/features";
import { CartItemModel } from "@/models/cart/type";
import { useClientFrontStoreCartItems } from "@/storage";
import { groupBy, sumBy } from "@/utils/array";
import { pushGtmEvent } from "@/utils/gtm";

/**
 * カートに追加する商品情報を元に新しいカートアイテムを生成する
 */
export const buildNewCartItems = (
  currentCartItems: CartItemModel[],
  products: VariantIdWithQuantity[],
  subscriptionEnabled: boolean
) => {
  const newItems = products.map((product) => {
    return {
      variantId: product.variantId,
      quantity: product.quantity,
      subscription: subscriptionEnabled,
    };
  });
  const newCartItems = [...currentCartItems, ...newItems];
  // 一旦variantIdごとに配列化する
  const groupedCartItems = groupBy(newCartItems, "variantId");
  return Object.entries(groupedCartItems).map(([variantId, cartItems]) => {
    const quantity = sumBy(cartItems, "quantity");
    return {
      variantId: Number(variantId),
      quantity,
      subscription: subscriptionEnabled,
    };
  });
};

type VariantIdWithQuantity = { variantId: number; quantity: number };

/**
 * カートへの追加、いますぐ購入の処理と状態を管理
 * @remarks
 * Client Side renderingでのみ対応
 */
export const useAddToCart = () => {
  const { cartItems, setCartItems } = useClientFrontStoreCartItems();
  const [cartAdded, setCartAdded] = useState(false);
  const { push } = useRouter();

  const addToCart = useCallback(
    async (newItems: VariantIdWithQuantity[], subscriptionEnabled: boolean) => {
      pushGtmEvent({
        event: "productPageClickButton",
        button_type: (subscriptionEnabled ? "subscription_" : "normal_") + "addCart",
      });

      const newCartItems = buildNewCartItems(cartItems, newItems, subscriptionEnabled);
      setCartItems(newCartItems);
      setCartAdded(true);
    },
    [cartItems, setCartItems]
  );

  const addToCartDirect = useCallback(
    async (
      newItems: VariantIdWithQuantity[],
      subscriptionEnabled: boolean,
      disabled: boolean,
      setName?: string
    ) => {
      pushGtmEvent({
        event: "productPageClickButton",
        button_type: (subscriptionEnabled ? "subscription_" : "normal_") + "directCheckout",
      });

      if (disabled) {
        return;
      }

      const newCartItems = buildNewCartItems(cartItems, newItems, subscriptionEnabled);
      setCartItems(newCartItems);

      push(buildCheckoutUrl(newCartItems, null, setName ? [setName] : []));
    },
    [cartItems, push, setCartItems]
  );

  return {
    cartAdded,
    addToCart,
    addToCartDirect,
  };
};

export const useHandleScroll = () => {
  const [isScrollLimit, setIsScrollLimit] = useState(true);
  const [showCTAButton, setShowCTAButton] = useState(false);

  const checkCTAButton = useCallback(() => {
    const ctaButtonSelector = ".appear_fixed_cta_button_boader";
    // 固定CTAボタンを表示する座標の取得
    const title = document.querySelector(ctaButtonSelector);
    if (!title) {
      return;
    }

    const targetRect = title.getBoundingClientRect().top;

    // 相対座標が0より小さくなったら表示する
    setShowCTAButton(targetRect < 0);
  }, []);

  const checkScrollLimit = useCallback(() => {
    const elem = document.getElementById("getHeight");
    if (elem) {
      const height = elem.offsetTop + 36;
      const scrollHeight = window.pageYOffset;
      if (scrollHeight > height) {
        setIsScrollLimit(false);
      } else {
        setIsScrollLimit(true);
      }
    }
  }, []);

  // NOTE: スタイルを直接いじっている
  const toggleProductCvFix = useCallback(() => {
    const bodyHeight = document.body.clientHeight;
    const productCvPart = document.getElementById("productCv");
    const footerPart = document.getElementById("footer");
    if (!productCvPart || !footerPart) {
      return;
    }
    const productCv = productCvPart.clientHeight;
    const footerHeight = footerPart.clientHeight;
    const newTop = bodyHeight - footerHeight - productCv - 140;
    if (window.scrollY > newTop) {
      productCvPart.style.position = "absolute";
      productCvPart.style.top = newTop + "px";
    } else {
      productCvPart.style.position = "fixed";
      productCvPart.style.top = "auto";
    }
  }, []);

  const handleScroll = useMemo(
    () =>
      throttle(200, () => {
        checkScrollLimit();
        checkCTAButton();
        toggleProductCvFix();
      }),
    [checkCTAButton, checkScrollLimit, toggleProductCvFix]
  );

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll]);

  return {
    isScrollLimit,
    showCTAButton,
  };
};
